
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Clock, CheckCircle2, AlertCircle, PlusCircle, TrendingUp, Users, Smartphone, ShieldCheck, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import dashboardApi from "../../../api/dashboardApi";
import repairApi from "../../../api/repairApi";
import toast from "react-hot-toast";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const Home = () => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const res = await dashboardApi.getSummary(period);
        if (res.success && res.data) {
          setSummaryData(res.data);
        } else {
          toast.error(res.message || "Lỗi tải thống kê");
        }
      } catch (err) {
        toast.error("Lỗi kết nối máy chủ");
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [period]);

  // Click outside to close search suggestions
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced Search
  useEffect(() => {
    if (searchTerm.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await repairApi.getAllRepairs("", searchTerm);
        if (res.success) {
          setSuggestions(res.data.slice(0, 5)); // show top 5 matches
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Search error", error);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  const formatCompact = (amount) => {
    if (amount >= 1000000000) return (amount / 1000000000).toFixed(1).replace('.0', '') + " Tỷ";
    if (amount >= 1000000) return (amount / 1000000).toFixed(1).replace('.0', '') + " Tr";
    if (amount >= 1000) return (amount / 1000).toFixed(1).replace('.0', '') + " N";
    return amount;
  };



  const getStatusIcon = (status) => {
    switch(status) {
      case "Completed": 
      case "Returned": return <CheckCircle2 size={18} className="text-emerald-500" />;
      case "Repairing":
      case "Quoted": return <Clock size={18} className="text-amber-500" />;
      case "CannotRepair":
      case "Cancelled": return <AlertCircle size={18} className="text-red-500" />;
      default: return <AlertCircle size={18} className="text-primary-500" />;
    }
  };

  const getStatusBg = (status) => {
    switch(status) {
      case "Completed": 
      case "Returned": return "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/50 dark:border-emerald-500/20 shadow-sm shadow-emerald-500/10 dark:shadow-emerald-500/5";
      case "Repairing":
      case "Quoted": return "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200/50 dark:border-amber-500/20 shadow-sm shadow-amber-500/10 dark:shadow-amber-500/5";
      case "CannotRepair":
      case "Cancelled": return "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-200/50 dark:border-red-500/20 shadow-sm shadow-red-500/10 dark:shadow-red-500/5";
      default: return "bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 border-primary-200/50 dark:border-primary-500/20 shadow-sm shadow-primary-500/10 dark:shadow-primary-500/5";
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
  };

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-8"
    >
      {/* Header & Search */}
      <motion.div variants={itemVariants} className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 dark:text-white tracking-tight">Tổng quan <span className="text-gradient">Doanh nghiệp</span></h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium mt-1">Cập nhật theo thời gian thực về tình hình cửa hàng.</p>
        </div>
        
        <div className="relative w-full md:w-96" ref={searchRef}>
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            {isSearching ? (
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Search size={20} className="text-slate-400" />
            )}
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => { if (searchTerm.trim().length > 0) setShowSuggestions(true); }}
            className="block w-full pl-11 pr-4 py-3.5 glass-card rounded-2xl text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all font-medium"
            placeholder="Tìm SĐT hoặc Mã phiếu..."
          />
          
          <AnimatePresence>
            {showSuggestions && (
              <motion.div 
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute z-50 top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden"
              >
                {suggestions.length === 0 ? (
                  <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">Không tìm thấy kết quả</div>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                    {suggestions.map((repair) => (
                      <div 
                        key={repair.id}
                        onClick={() => navigate(`/repairs/${repair.id}`)}
                        className="p-3 border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer flex justify-between items-center transition-colors group"
                      >
                        <div className="truncate">
                          <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{repair.repairCode} - {repair.deviceName}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{repair.customerName} • {repair.customerPhone}</p>
                        </div>
                        <ChevronRight size={16} className="text-slate-400 group-hover:text-primary-500 transition-colors flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Epic Stats Cards */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <div className="relative overflow-hidden bg-gradient-to-br from-primary-500 to-primary-700 rounded-3xl p-6 text-white shadow-lg shadow-primary-500/40">
          <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
          <ShieldCheck className="text-primary-200 mb-4" size={28} />
          <h3 className="text-primary-100 text-xs font-bold mb-1 uppercase tracking-widest">Đang chờ / Đang sửa</h3>
          <div className="flex items-end space-x-2">
            <p className="text-4xl font-black tracking-tighter">{summaryData?.totalPending || 0}</p>
            <span className="text-sm font-medium text-primary-200 mb-1 leading-tight">phiếu</span>
          </div>
        </div>
        
        <div className="glass-card rounded-3xl p-6 relative overflow-hidden group hover:border-primary-300 transition-colors">
          <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-emerald-400/20 rounded-full blur-xl group-hover:scale-150 transition-transform"></div>
          <CheckCircle2 className="text-emerald-500 mb-4" size={28} />
          <h3 className="text-slate-500 dark:text-slate-400 text-xs font-bold mb-1 uppercase tracking-widest">Hoàn thành</h3>
          <div className="flex items-end space-x-2">
            <p className="text-4xl font-black text-slate-800 dark:text-white tracking-tighter">{summaryData?.totalCompleted || 0}</p>
            <span className={`text-sm font-bold mb-1 leading-tight ${summaryData?.completedGrowth >= 0 ? "text-emerald-600" : "text-red-500"}`}>
              {summaryData?.completedGrowth >= 0 ? "+" : ""}{summaryData?.completedGrowth || 0}%
            </span>
          </div>
        </div>

        <div className="glass-card rounded-3xl p-6 relative overflow-hidden group hover:border-primary-300 transition-colors">
          <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-amber-400/20 rounded-full blur-xl group-hover:scale-150 transition-transform"></div>
          <TrendingUp className="text-amber-500 mb-4" size={28} />
          <h3 className="text-slate-500 dark:text-slate-400 text-xs font-bold mb-1 uppercase tracking-widest">Doanh thu</h3>
          <div className="flex items-end space-x-2">
            <p className="text-3xl font-black text-slate-800 dark:text-white tracking-tighter">{formatCompact(summaryData?.totalRevenue || 0)}</p>
            <span className={`text-sm font-bold mb-1 leading-tight ${summaryData?.revenueGrowth >= 0 ? "text-amber-600" : "text-red-500"}`}>
              {summaryData?.revenueGrowth >= 0 ? "+" : ""}{summaryData?.revenueGrowth || 0}%
            </span>
          </div>
        </div>

        <div className="glass-card rounded-3xl p-6 relative overflow-hidden group hover:border-primary-300 transition-colors">
          <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-purple-400/20 rounded-full blur-xl group-hover:scale-150 transition-transform"></div>
          <Users className="text-purple-500 mb-4" size={28} />
          <h3 className="text-slate-500 dark:text-slate-400 text-xs font-bold mb-1 uppercase tracking-widest">Tổng khách hàng</h3>
          <div className="flex items-end space-x-2">
            <p className="text-4xl font-black text-slate-800 dark:text-white tracking-tighter">{summaryData?.newCustomers || 0}</p>
            <span className={`text-sm font-bold mb-1 leading-tight ${summaryData?.customerGrowth >= 0 ? "text-purple-600" : "text-red-500"}`}>
              {summaryData?.customerGrowth >= 0 ? "+" : ""}{summaryData?.customerGrowth || 0}%
            </span>
          </div>
        </div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        
        {/* Chart Section */}
        <motion.div variants={itemVariants} className="lg:col-span-2 glass-card rounded-3xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-800 dark:text-white tracking-tight">Biểu đồ Doanh Thu</h2>
            <select 
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500/20 font-medium cursor-pointer"
            >
              <option value="week">Tuần này</option>
              <option value="month">Tháng này</option>
              <option value="year">Năm nay</option>
            </select>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summaryData?.chartData || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorDoanhThu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ borderRadius: "16px", border: "none", boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" }}
                />
                <Area type="monotone" dataKey="doanhThu" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorDoanhThu)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Recent List Section */}
        <motion.div variants={itemVariants} className="lg:col-span-1 glass-card rounded-3xl p-6 flex flex-col h-full">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-800 dark:text-white tracking-tight">Phiếu gần đây</h2>
            <Link to="/repairs" className="text-sm font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 transition-colors">Xem tất cả</Link>
          </div>
          
          <div className="space-y-4 flex-1">
            {(summaryData?.recentRepairs || []).map((repair, index) => (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                key={repair.id}
                onClick={() => navigate(`/repairs/${repair.id}`)}
                className="group bg-white/60 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-700 rounded-2xl p-4 border border-slate-100 dark:border-slate-700 shadow-sm flex items-center justify-between transition-all hover:shadow-md cursor-pointer hover:border-primary-200 dark:hover:border-primary-800"
              >
                <div className="flex items-center space-x-4 overflow-hidden">
                  <div className={`p-3 rounded-xl border ${getStatusBg(repair.status)} group-hover:scale-110 transition-transform duration-300`}>
                    {getStatusIcon(repair.status)}
                  </div>
                  <div className="truncate">
                    <h3 className="text-[15px] font-bold text-slate-800 dark:text-white truncate">{repair.device}</h3>
                    <div className="flex items-center text-xs text-slate-500 dark:text-slate-400 mt-1 space-x-2">
                      <span className="font-semibold text-slate-600 dark:text-slate-300">{repair.customer}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                      <span>{repair.time}</span>
                    </div>
                  </div>
                </div>
                <div className="text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 group-hover:translate-x-1 transition-all flex-shrink-0 ml-2">
                  <ChevronRight size={18} />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      
      {/* FAB - Floating Action Button for easy reach on mobile */}
      <Link to="/create" className="fixed bottom-[100px] right-6 w-14 h-14 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full shadow-[0_0_20px_rgba(59,130,246,0.5)] flex items-center justify-center text-white active:scale-95 hover:scale-105 transition-all z-50 md:hidden border-2 border-white/20">
        <PlusCircle size={24} />
      </Link>
    </motion.div>
  );
};

export default Home;

